import { useMemo } from "react";
import { Loading } from "../../components/Loading";
import {
  Box,
  Button,
  Table,
  TableContainer,
  Tbody,
  Text,
  Tfoot,
  Th,
  Thead,
  Tr,
} from "@chakra-ui/react";
import { formatBRL } from "../../utils/currency";
import { Pagination } from "../../components/Pagination";
import { Item } from "./Item";
import { api } from "../../libs/axios";
import { saveAs } from 'file-saver';
import Swal from "sweetalert2";
import { getToken, getUser, isRepresentante, shouldHideFeeDetails } from "../../libs/auth";
import { getAgenciasFromUser } from "../../utils/agency";
import { RelatorioFilter } from "../../hooks/relatorio/useRelatorio";
import { MetaPagination, Pedido } from "../../utils/interfaces";

type AgenciaWithExcelTag = {
  id?: string | number;
  value?: string | number;
  relatorio_excel_habilitado?: boolean | number | string;
  stur_enabled?: boolean | number | string;
};

function getAgenciaId(agencia: unknown): string | undefined {
  if (agencia === null || agencia === undefined) return undefined;
  if (typeof agencia === "string" || typeof agencia === "number") return String(agencia);
  if (typeof agencia !== "object") return undefined;

  const item = agencia as AgenciaWithExcelTag;
  if (item.id !== undefined) return String(item.id);
  if (item.value !== undefined) return String(item.value);

  return undefined;
}

function parseBoolean(value: unknown): boolean {
  if (typeof value === "boolean") return value;
  if (typeof value === "number") return value === 1;
  if (typeof value === "string") {
    const normalized = value.trim().toLowerCase();
    return normalized === "1" || normalized === "true";
  }

  return false;
}

function isRelatorioExcelHabilitado(agencia: unknown): boolean {
  if (!agencia || typeof agencia !== "object") return false;

  const value = (agencia as AgenciaWithExcelTag).relatorio_excel_habilitado;
  return parseBoolean(value);
}

function isSturEnabled(agencia: unknown): boolean {
  if (!agencia || typeof agencia !== "object") return false;

  const value = (agencia as AgenciaWithExcelTag).stur_enabled;
  return parseBoolean(value);
}

type ListaProps = {
  data?: {
    pedidos: Pedido[];
    meta: MetaPagination;
  };
  dataResumo?: {
    transacoes?: number;
    assentos?: number;
    total?: number;
    comissao_total?: number;
    comissao_representante?: number;
  };
  filter?: RelatorioFilter;
  isLoading: boolean;
  setPage: (page: number) => void;
};

export function Lista({
  data,
  dataResumo,
  filter,
  isLoading,
  setPage,
}: ListaProps) {
  const user = getUser();
  const representanteOnly = isRepresentante();
  const hideFeeDetails = shouldHideFeeDetails();
  const agencias = useMemo(() => getAgenciasFromUser(user), [user]);
  const hasFiliais = useMemo(() => agencias.length > 1, [agencias]);
  const agenciaSelecionada = useMemo(() => {
    const agenciaSelecionadaId = filter?.agencia ? String(filter.agencia) : undefined;

    if (agenciaSelecionadaId) {
      const agenciaSelecionadaByFiltro = agencias.find(
        (agencia) => getAgenciaId(agencia) === agenciaSelecionadaId,
      );

      if (agenciaSelecionadaByFiltro) {
        return agenciaSelecionadaByFiltro;
      }
    }

    return (user as { agencia?: unknown } | undefined)?.agencia;
  }, [agencias, filter?.agencia, user]);
  const canExportXlsx = useMemo(() => {
    const agenciaSelecionadaId = filter?.agencia ? String(filter.agencia) : undefined;

    if (agenciaSelecionadaId) {
      const agenciaSelecionada = agencias.find(
        (agencia) => getAgenciaId(agencia) === agenciaSelecionadaId,
      );

      if (agenciaSelecionada) {
        return isRelatorioExcelHabilitado(agenciaSelecionada);
      }

      const agenciaUsuario = (user as { agencia?: unknown } | undefined)?.agencia;
      if (getAgenciaId(agenciaUsuario) === agenciaSelecionadaId) {
        return isRelatorioExcelHabilitado(agenciaUsuario);
      }

      return false;
    }

    const agenciaUsuario = (user as { agencia?: unknown } | undefined)?.agencia;
    return isRelatorioExcelHabilitado(agenciaUsuario);
  }, [agencias, filter?.agencia, user]);
  const canShowSturButton = useMemo(() => {
    if (representanteOnly || hideFeeDetails) {
      return false;
    }

    if (hasFiliais && !filter?.agencia) {
      return false;
    }

    return isSturEnabled(agenciaSelecionada);
  }, [agenciaSelecionada, filter?.agencia, hasFiliais, hideFeeDetails, representanteOnly]);
  const visibleColumnsCount =
    2 + // Pedido, Data Compra
    (representanteOnly ? 0 : 1) + // Usuario
    (hasFiliais ? 1 : 0) + // Agencia
    (representanteOnly ? 0 : 1) + // Forma Pagto
    1 + // Status
    1 + // Bilhetes
    (representanteOnly || hideFeeDetails ? 0 : 1) + // Ordem de Trabalho
    (hideFeeDetails ? 0 : 1) + // Comissao
    1; // Valor

  if (isLoading) return <Loading />;

  const totaisPagina = data?.pedidos.reduce((acc, item) => {
    const valorRelatorio = representanteOnly
      ? item.valor_base_comissao_representante ?? item.valor_total
      : item.valor_total;

    acc.pedidos += Number(valorRelatorio);
    acc.comissao += hideFeeDetails ? 0 : Number(item.comissao ?? 0);
    return acc;
  }, {
    comissao: 0,
    pedidos: 0,
  });

  const getFileNameFromHeaders = (
    contentDisposition: string | undefined,
    fallback: string
  ) => {
    if (!contentDisposition) return fallback;

    const utf8Match = contentDisposition.match(/filename\*\s*=\s*UTF-8''([^;]+)/i);
    if (utf8Match?.[1]) {
      return decodeURIComponent(utf8Match[1].replace(/["']/g, "").trim());
    }

    const plainMatch = contentDisposition.match(/filename\s*=\s*("?)([^";]+)\1/i);
    if (plainMatch?.[2]) {
      return plainMatch[2].trim();
    }

    return fallback;
  };

  const normalizeDateForFileName = (value?: unknown) => {
    if (!value) return "";

    if (value instanceof Date && !Number.isNaN(value.getTime())) {
      const dia = String(value.getDate()).padStart(2, "0");
      const mes = String(value.getMonth() + 1).padStart(2, "0");
      const ano = value.getFullYear();
      return `${dia}-${mes}-${ano}`;
    }

    if (typeof value !== "string") return "";

    return value
      .trim()
      .replace(/\//g, "-")
      .replace(/[^A-Za-z0-9_-]+/g, "-")
      .replace(/^-+|-+$/g, "");
  };

  const normalizeDateForStur = (value?: unknown) => {
    if (!value) return undefined;

    if (value instanceof Date && !Number.isNaN(value.getTime())) {
      const dia = String(value.getDate()).padStart(2, "0");
      const mes = String(value.getMonth() + 1).padStart(2, "0");
      const ano = value.getFullYear();
      return `${ano}-${mes}-${dia}`;
    }

    if (typeof value !== "string") return undefined;

    const normalized = value.trim();
    const brDateMatch = normalized.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
    if (brDateMatch) {
      const [, dia, mes, ano] = brDateMatch;
      return `${ano}-${mes}-${dia}`;
    }

    if (/^\d{4}-\d{2}-\d{2}$/.test(normalized)) {
      return normalized;
    }

    return undefined;
  };

  const handleExport = async (formato: "csv" | "xlsx") => {
    const dataInicial = normalizeDateForFileName(filter?.data_inicial);
    const dataFinal = normalizeDateForFileName(filter?.data_final);
    const nomeArquivoFallback = formato === "xlsx" && dataInicial && dataFinal
      ? `relatorio_${dataInicial}_a_${dataFinal}.xlsx`
      : `pedidos.${formato}`;

    Swal.fire({
      title: `Exportar ${formato.toUpperCase()}`,
      text: "Para uma exportação mais rápida, você pode realizar o filtro de pedidos no formulário. Confirme e aguarde que o arquivo será baixado automaticamente no seu navegador!",
      showCancelButton: true,
      cancelButtonText: 'Cancelar',
      confirmButtonText: "Iniciar download",
      showLoaderOnConfirm: true,
      preConfirm: async () => {
        try {
          const response = await api.get('pedido/download', {
            params: {
              ...filter,
              formato,
            },
            responseType: 'blob'
          });

          const nomeArquivo = getFileNameFromHeaders(
            response.headers?.["content-disposition"],
            nomeArquivoFallback
          );

          saveAs(response.data, nomeArquivo);
        } catch (error) {
          console.log(error)
          Swal.showValidationMessage(`
            Não foi possível completar o download, tente novamente mais tarde!
          `);
        }
      },
      allowOutsideClick: () => !Swal.isLoading()
    });
  };

  const handleExportSturXml = async () => {
    const agenciaId = getAgenciaId(agenciaSelecionada);
    const sturToken = getToken();
    const dataInicio = normalizeDateForStur(filter?.data_inicial);
    const dataFim = normalizeDateForStur(filter?.data_final);

    const statusFilter = String(filter?.status ?? "").trim();
    const statusStur =
      statusFilter === "3" ? "finalizado"
        : statusFilter === "4" ? "cancelado"
          : undefined;

    Swal.fire({
      title: "Gerar XML STUR",
      text: "Confirme para gerar o XML STUR com os filtros aplicados no relatório.",
      showCancelButton: true,
      cancelButtonText: "Cancelar",
      confirmButtonText: "Gerar XML",
      showLoaderOnConfirm: true,
      preConfirm: async () => {
        if (hasFiliais && !filter?.agencia) {
          Swal.showValidationMessage("Selecione uma agência no filtro para gerar o XML STUR.");
          return;
        }

        if (!agenciaId) {
          Swal.showValidationMessage("Selecione uma agência válida para gerar o XML STUR.");
          return;
        }

        if (!isSturEnabled(agenciaSelecionada)) {
          Swal.showValidationMessage("A agência selecionada não está habilitada para STUR.");
          return;
        }

        if (!sturToken) {
          Swal.showValidationMessage("Sessão inválida. Faça login novamente para gerar o XML STUR.");
          return;
        }

        if (!dataInicio || !dataFim) {
          Swal.showValidationMessage("Informe Data Inicial e Data Final válidas para gerar o XML STUR.");
          return;
        }

        if (statusFilter && !statusStur) {
          Swal.showValidationMessage("STUR aceita apenas os status Finalizado ou Cancelado.");
          return;
        }

        const fallbackName = `stur_${agenciaId}_${dataInicio}_a_${dataFim}.xml`;

        try {
          const response = await api.post(
            "stur",
            {
              token: sturToken,
              agencia: Number(agenciaId),
              data_inicio: dataInicio,
              data_fim: dataFim,
              ...(statusStur ? { status: statusStur } : {}),
            },
            {
              responseType: "blob",
              headers: {
                Accept: "application/xml",
              },
            },
          );

          const nomeArquivo = getFileNameFromHeaders(
            response.headers?.["content-disposition"],
            fallbackName,
          );

          saveAs(response.data, nomeArquivo);
        } catch (error) {
          console.log(error);
          Swal.showValidationMessage("Não foi possível gerar o XML STUR, tente novamente mais tarde.");
        }
      },
      allowOutsideClick: () => !Swal.isLoading(),
    });
  };

  const totalComissao = hideFeeDetails ? 0 : dataResumo?.comissao_total ?? totaisPagina?.comissao ?? 0;
  const totalValor = dataResumo?.total ?? totaisPagina?.pedidos ?? 0;

  return (
    <>
      <Box
        gap={8}
        fontSize={"sm"}
        p={"5"}
        border={"1px solid #ccc"}
        display={"flex"}
        flexDir={{
          xs: 'column',
          md: 'row',
        }}
      >
        <Box
          width={"full"}
          fontSize={"sm"}
          p={"5"}
          border={"1px solid #ccc"}
          display={"flex"}
          flexDir={"column"}
          alignItems={"flex-start"}
        >
          <Box
            mt={"-8"}
            fontWeight={"bold"}
            bg={"white"}
            as={"span"}
            w={"70px"}
            display={"flex"}
            alignItems={"flex-start"}
            justifyContent={"flex-start"}
          >
            Resumo
          </Box>
          <Text>Pedidos: {dataResumo?.transacoes}</Text>
          <Text>Bilhetes: {dataResumo?.assentos}</Text>
          {!representanteOnly && !hideFeeDetails && (
            <Text>Comissão: {formatBRL(dataResumo?.comissao_total ?? 0)}</Text>
          )}
          {representanteOnly && !hideFeeDetails && (
            <Text>Comissão Representante: {formatBRL(dataResumo?.comissao_representante ?? 0)}</Text>
          )}
          <Text>{representanteOnly ? "Valor Base" : "Valor Total"}: {formatBRL(dataResumo?.total ?? 0)}</Text>
        </Box>
        <Box
          width={"full"}
          fontSize={"sm"}
          p={"5"}
          border={"1px solid #ccc"}
          display={"flex"}
          flexDir={"column"}
          alignItems={"flex-start"}
        >
          <Box
            mt={"-8"}
            fontWeight={"bold"}
            bg={"white"}
            as={"span"}
            w={"70px"}
            display={"flex"}
            alignItems={"flex-start"}
            justifyContent={"flex-start"}
          >
            Ações
          </Box>
          {!representanteOnly && !hideFeeDetails && (
            <Box display={"flex"} gap={2} flex={1} alignItems={"center"} justifyContent={"center"} width={"full"}>
              <Button
                type="button"
                onClick={() => handleExport("csv")}
                colorScheme="blue"
                borderRadius="none"
                size="sm"
              >
                Exportar CSV
              </Button>
              {canExportXlsx && (
                <Button
                  type="button"
                  onClick={() => handleExport("xlsx")}
                  colorScheme="green"
                  borderRadius="none"
                  size="sm"
                >
                  Exportar XLSX
                </Button>
              )}
              {canShowSturButton && (
                <Button
                  type="button"
                  onClick={handleExportSturXml}
                  colorScheme="orange"
                  borderRadius="none"
                  size="sm"
                >
                  Gerar XML STUR
                </Button>
              )}
            </Box>
          )}
        </Box>
        <Box
          width={"full"}
          fontSize={"sm"}
          p={"5"}
          border={"1px solid #ccc"}
          display={"flex"}
          flexDir={"column"}
        >
          <Box
            mt={"-8"}
            fontWeight={"bold"}
            bg={"white"}
            as={"span"}
            w={"70px"}
            display={"flex"}
            alignItems={"flex-start"}
            justifyContent={"flex-start"}
          >
            Paginação
          </Box>
          <Pagination
            onPageChange={setPage}
            currentPage={data?.meta.current_page}
            lastPage={data?.meta.last_page}
            numberOfItens={data?.pedidos.length}
            numberToPage={data?.meta.to}
            registerPerPage={data?.meta.per_page}
            totalCountofRegisters={data?.meta.total}
          />
        </Box>
      </Box>
      <TableContainer>
        <Table variant={"striped"}>
          <Thead>
            <Tr color={'white'}>
              <Th bg={'#525252'} color={'white'} p={0} textAlign={'center'}>PEDIDO</Th>
              <Th bg={'#525252'} color={'white'} p={0} textAlign={'center'}>DATA COMPRA</Th>
              {!representanteOnly && (
                <Th bg={'#525252'} color={'white'} p={0} textAlign={'center'}>USUÁRIO</Th>
              )}
              {hasFiliais && (
                <Th bg={'#525252'} color={'white'} p={0} textAlign={'center'}>AGÊNCIA</Th>
              )}
              {!representanteOnly && (
                <Th bg={'#525252'} color={'white'} p={0} textAlign={'center'}>FORMA PAGTO</Th>
              )}
              <Th bg={'#525252'} color={'white'} p={0} textAlign={'center'}>STATUS</Th>
              <Th bg={'#525252'} color={'white'} p={0} textAlign={'center'}>BILHETES</Th>
              {!representanteOnly && !hideFeeDetails && (
                <Th bg={'#525252'} color={'white'} p={0} textAlign={'center'}>ORDEM DE TRABALHO</Th>
              )}
              {!hideFeeDetails && (
                <Th bg={'#525252'} color={'white'} p={0} textAlign={'center'}>COMISSÃO</Th>
              )}
              <Th bg={'#525252'} color={'white'} p={0} textAlign={'center'}>{representanteOnly ? "VALOR BILHETE" : "VALOR"}</Th>
        
            </Tr>
          </Thead>
          <Tbody>
            {data?.pedidos.map(item => (
              <Item key={item.id} pedido={item} showAgencia={hasFiliais} representanteOnly={representanteOnly} hideFeeDetails={hideFeeDetails} />
            ))}
          </Tbody>
          <Tfoot>
            <Tr>
              <Th
                bg={'#525252'}
                color={'white'}
                p={0}
                textAlign={'center'}
                colSpan={Math.max(1, visibleColumnsCount - (hideFeeDetails ? 2 : 3))}
              ></Th>
              <Th bg={'#525252'} color={'white'} p={0} textAlign={'center'}>TOTAL</Th>
              {!hideFeeDetails && (
                <Th bg={'#525252'} color={'white'} p={0} textAlign={'center'}>
                  <Text fontWeight={'bold'}>{formatBRL(totalComissao)}</Text>
                </Th>
              )}
              <Th bg={'#525252'} color={'white'} p={0} textAlign={'center'}>
                <Text fontWeight={'bold'}>{formatBRL(totalValor)}</Text>
              </Th>
            </Tr>
          </Tfoot>
        </Table>
      </TableContainer>
    </>
  );
}
